<script>

import {getPreferredLocale} from "@/utils/locale.js";

export default {
    data() {
        return {
            loading: false,
            loaded: false,
            errors: {},
            success: false,
            amount: 0,
            value: null,
            currency: '',
            comments: this.$t('withdrawal_requests.messages.default_comment'),
            company_id: null,
            defaultComments: this.getDefaultComments(),
        }
    },

    methods: {
        getDefaultComments() {
            let defaultComments = import.meta.env['VITE_WITHDRAWAL_COMMENTS_' + getPreferredLocale().toUpperCase()];
            if (!defaultComments) {
                return [];
            }

            return defaultComments.split(',').sort(() => Math.random() - 0.5).slice(0, 3).map(item => item.trim());
        },
        sendRequest() {
            this.loading = true;
            axios.post('/withdrawal-requests', {
                amount: this.amount,
                value: this.value,
                currency: this.currency,
                comments: this.comments,
                company_id: this.company_id,
            }).then(() => {
                this.$auth.user.token_balance = 0;
                this.success = true;
            }).catch((error) => {
                if (error.response?.data?.error) {
                    this.$toast.open({ message: error.response.data.error, type: 'error' });
                } else {
                    this.$toast.error(this.$t('messages.error_try_again'))
                }
            }).finally(() => {
                this.loading = false;
            })
        },
        close() {
            this.$emit('close', {requestSent: this.success});
        }
    },
    async mounted() {
        this.loaded = false;
        await this.$auth.fetchUser();
        this.company_id = this.$auth.user.companies?.[0]?.id ?? null;
        this.loaded = true;
    }
};
</script>

<template>
    <section>
        <div class="modal is-active">
            <div class="modal-background"></div>
            <div class="modal-card">
                <header class="modal-card-head">
                    <p class="modal-card-title">{{ $t('withdrawal_requests.labels.withdrawal_request') }}</p>
                    <button class="delete" aria-label="close" @click="close"></button>
                </header>
                <form @submit.prevent="sendRequest">
                    <div class="modal-card-body">
                        <loader :loading="!loaded"></loader>

                        <div v-if="loaded">
                            <div v-if="success" class="notification is-success is-size-6">
                                {{ $t('withdrawal_requests.messages.request_successful') }}
                            </div>
                            <div v-if="!success">
                                <section
                                    v-if="$auth.user.bank_account_number && $auth.user.token_balance > 0">
                                    <div class="block">
                                        <b-field v-if="$auth.user.companies && $auth.user.companies.length > 1"
                                                 :label="$t('companies.labels.company')"
                                                 :type="errors.company_id ? 'is-danger' : null"
                                                 :message="errors.company_id ? errors.company_id[0] : null">
                                            <b-select v-model="company_id" expanded required>
                                                <option v-for="company in $auth.user.companies"
                                                        :key="company.id"
                                                        :value="company.id">
                                                    {{ company.name }}
                                                </option>
                                            </b-select>
                                        </b-field>

                                        <b-field :label="$t('token_transactions.fields.amount_to_send.name')"
                                                 :type="errors.amount ? 'is-danger' : null"
                                                 :message="errors.amount ? errors.amount[0] : 'Max: ' + $auth.user.token_balance">
                                            <b-input
                                                v-model="amount"
                                                type="number"
                                                min="1"
                                                :max="$auth.user.token_balance"
                                                icon="cash"
                                            ></b-input>
                                        </b-field>
                                        <b-field :label="$t('token_transactions.fields.value.name')"
                                                 :type="errors.value ? 'is-danger' : null"
                                                 :message="errors.value ? errors.value[0] : null">
                                            <b-input
                                                v-model="value"
                                                icon="cash-multiple"
                                            ></b-input>
                                        </b-field>
                                        <b-field :label="$t('token_transactions.fields.currency.name')"
                                                 :type="errors.currency ? 'is-danger' : null"
                                                 :message="errors.currency ? errors.currency[0] : null">
                                            <b-input
                                                v-model="currency"
                                                icon="currency-usd"
                                            ></b-input>
                                        </b-field>
                                        <b-field :label="$t('token_transactions.fields.comments.name')"
                                                 :type="errors.comments ? 'is-danger' : null"
                                                 :message="errors.comments ? errors.comments[0] : null">
                                            <b-input
                                                rows="2"
                                                type="textarea"
                                                v-model="comments"
                                            ></b-input>
                                        </b-field>
                                        <div class="buttons" v-if="defaultComments.length">
                                            <b-button size="is-small"
                                                      v-for="defaultComment in defaultComments"
                                                      @click="comments = defaultComment">
                                                {{ defaultComment }}
                                            </b-button>
                                        </div>
                                    </div>

                                </section>
                                <section v-if="!$auth.user.bank_account_number">
                                    {{ $t('users.labels.bank_account_number_missing') }}
                                </section>
                                <section v-if="$auth.user.token_balance <= 0">
                                    {{ $t('messages.not_enough_tokens') }}
                                </section>
                            </div>
                        </div>
                    </div>
                    <footer class="modal-card-foot" v-if="loaded">
                        <div class="buttons">
                            <b-button
                                v-if="!$auth.user.bank_account_number"
                                @click="$router.push({name: 'profile.edit'})"
                                type="is-link">
                                {{ $t('actions.go_to_edit_profile') }}
                            </b-button>
                            <b-button
                                native-type="submit"
                                v-if="$auth.user.bank_account_number && !success"
                                :disabled="loading || success"
                                :loading="loading"
                                type="is-success">
                                {{ $t('actions.send') }}
                            </b-button>
                            <b-button v-if="!success" @click.prevent="close">
                                {{ $t('actions.cancel') }}
                            </b-button>
                            <b-button type="is-success" v-if="success" @click.prevent="close">
                                {{ $t('actions.ok') }}
                            </b-button>
                        </div>
                    </footer>
                </form>
            </div>
        </div>
    </section>
</template>
<style scoped>
.modal-card {
    min-height: 300px;
}
</style>
